import { CreditCard, Plus, Trash2 } from 'lucide-react';
import { formatCurrencyOption } from '@/utils/formatters';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useFxCurrencies } from '@/hooks/react-query/useFxRates';
import type { BankAccountFormData } from './types';

interface BankAccountsSectionProps {
  bankAccounts: BankAccountFormData[];
  onAdd: () => void;
  onUpdate: (
    index: number,
    field: keyof BankAccountFormData,
    value: string | boolean
  ) => void;
  onRemove: (index: number) => void;
  onSetPrimary: (index: number) => void;
}

const DEFAULT_CURRENCY_OPTIONS = [
  { iso_code: 'EUR', name: 'Euro' },
  { iso_code: 'USD', name: 'US Dollar' },
  { iso_code: 'GBP', name: 'British Pound' },
  { iso_code: 'CHF', name: 'Swiss Franc' },
];

export function BankAccountsSection({
  bankAccounts,
  onAdd,
  onUpdate,
  onRemove,
  onSetPrimary,
}: BankAccountsSectionProps) {
  const { data: fxCurrencies = [] } = useFxCurrencies();
  const currencyOptions =
    fxCurrencies.length > 0 ? fxCurrencies : DEFAULT_CURRENCY_OPTIONS;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-slate-700" />
            <CardTitle>Bank Accounts</CardTitle>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onAdd}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Account
          </Button>
        </div>
        <CardDescription>
          Manage bank accounts associated with this fund
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {bankAccounts.length === 0 ? (
          <div className="text-center py-8 border border-dashed rounded-lg">
            <p className="text-gray-500 mb-4">No bank accounts added yet.</p>
            <div className="flex justify-center">
              <Button
                type="button"
                variant="outline"
                onClick={onAdd}
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add First Account
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {bankAccounts.map((account, index) => (
              <div key={account.id} className="border rounded-lg p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium">Bank Account {index + 1}</h4>
                  <div className="flex items-center gap-2">
                    {!account.is_primary && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => onSetPrimary(index)}
                      >
                        Set Primary
                      </Button>
                    )}
                    {account.is_primary && (
                      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                        Primary
                      </span>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => onRemove(index)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor={`account_name_${index}`}>
                      Account Name
                    </Label>
                    <Input
                      id={`account_name_${index}`}
                      value={account.account_name}
                      onChange={(e) =>
                        onUpdate(index, 'account_name', e.target.value)
                      }
                      placeholder="Fund Capital Account"
                    />
                  </div>
                  <div />
                  <div>
                    <Label htmlFor={`bank_name_${index}`}>Bank Name</Label>
                    <Input
                      id={`bank_name_${index}`}
                      value={account.bank_name}
                      onChange={(e) =>
                        onUpdate(index, 'bank_name', e.target.value)
                      }
                      placeholder="ABN AMRO Bank"
                    />
                  </div>
                  <div>
                    <Label htmlFor={`currency_${index}`}>Currency</Label>
                    <Select
                      value={account.currency}
                      onValueChange={(value) =>
                        onUpdate(index, 'currency', value)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {currencyOptions.map((currency) => (
                          <SelectItem
                            key={currency.iso_code}
                            value={currency.iso_code}
                          >
                            {formatCurrencyOption(currency)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor={`iban_${index}`}>IBAN</Label>
                    <Input
                      id={`iban_${index}`}
                      value={account.iban}
                      onChange={(e) => onUpdate(index, 'iban', e.target.value)}
                      placeholder="NL91 ABNA 0417 1643 00"
                    />
                  </div>
                  <div>
                    <Label htmlFor={`swift_bic_${index}`}>SWIFT/BIC</Label>
                    <Input
                      id={`swift_bic_${index}`}
                      value={account.swift_bic}
                      onChange={(e) =>
                        onUpdate(index, 'swift_bic', e.target.value)
                      }
                      placeholder="ABNANL2A"
                    />
                  </div>
                  <div>
                    <Label htmlFor={`account_number_${index}`}>
                      Account Number
                    </Label>
                    <Input
                      id={`account_number_${index}`}
                      value={account.account_number}
                      onChange={(e) =>
                        onUpdate(index, 'account_number', e.target.value)
                      }
                      placeholder="0417164300"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
